import { useState, useEffect } from 'react';
import { Show, useRecordContext, useNotify } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import TimerOffIcon from '@mui/icons-material/TimerOff';

const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3000';

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  completed: { label: 'Tamamlandı', color: '#28c76f', bg: 'rgba(40, 199, 111, 0.12)' },
  pending: { label: 'Bekliyor', color: '#ff9f43', bg: 'rgba(255, 159, 67, 0.12)' },
  cancelled: { label: 'İptal', color: '#ea5455', bg: 'rgba(234, 84, 85, 0.12)' },
  timeout: { label: 'Zaman Aşımı', color: '#676d7d', bg: 'rgba(103, 109, 125, 0.12)' },
};

interface ActivationItem {
  _id: string;
  service: string;
  serviceName: string;
  logo: string | null;
  phoneNumber: string;
  code: string | null;
  status: string;
  cost: number;
  country: number;
  countryCode?: string;
  countryIso?: string;
  createdAt: string;
}

interface ActivationStats {
  total: number;
  completed: number;
  pending: number;
  cancelled: number;
  timeout: number;
}

const StatCard = ({ label, value, color, bg, icon }: { label: string; value: number; color: string; bg: string; icon: React.ReactNode }) => (
  <Card sx={{ flex: 1, minWidth: 120 }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="caption" sx={{ color: '#676d7d' }}>{label}</Typography>
          <Typography variant="h5" sx={{ color, fontWeight: 700 }}>{value}</Typography>
        </Box>
        <Avatar sx={{ width: 40, height: 40, backgroundColor: bg }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const UserDetailContent = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [activations, setActivations] = useState<ActivationItem[]>([]);
  const [stats, setStats] = useState<ActivationStats | null>(null);
  const [activationsLoading, setActivationsLoading] = useState(true);

  useEffect(() => {
    if (!record?.id) return;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    fetch(`${API_URL}/api/users/${record.id}/activations`, { headers })
      .then(res => {
        if (!res.ok) throw new Error('Activations fetch failed');
        return res.json();
      })
      .then(data => {
        setActivations(data?.data || []);
        setStats(data?.stats || null);
      })
      .catch(() => {
        // silently fail, show empty state
      })
      .finally(() => setActivationsLoading(false));
  }, [record?.id, notify]);

  if (!record) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* User Info Card */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 2.5, borderBottom: '1px solid #404656', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, backgroundColor: 'rgba(115, 103, 240, 0.12)' }}>
              <PersonIcon sx={{ color: '#7367f0', fontSize: 20 }} />
            </Avatar>
            <Typography variant="h6" sx={{ color: '#d0d2d6', fontWeight: 600 }}>
              Kullanıcı Bilgileri
            </Typography>
          </Box>
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2.5 }}>
              <InfoItem label="UUID" value={record.uuid} />
              <InfoItem label="Firebase UID" value={record.firebaseUid} />
              <Box>
                <Typography variant="caption" sx={{ color: '#676d7d', mb: 0.5, display: 'block' }}>Durum</Typography>
                <Chip
                  label={record.isActive !== false ? 'Aktif' : 'Pasif'}
                  size="small"
                  sx={{
                    backgroundColor: record.isActive !== false ? 'rgba(40, 199, 111, 0.12)' : 'rgba(234, 84, 85, 0.12)',
                    color: record.isActive !== false ? '#28c76f' : '#ea5455',
                    fontWeight: 600,
                  }}
                />
              </Box>
              <InfoItem label="Oluşturulma Tarihi" value={record.createdAt ? new Date(record.createdAt).toLocaleString('tr-TR') : '-'} />
              <InfoItem label="Son Senkronizasyon" value={record.lastSyncAt ? new Date(record.lastSyncAt).toLocaleString('tr-TR') : '-'} />
              <InfoItem label="FCM Token" value={record.fcmToken || '-'} />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Activations Card */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 2.5, borderBottom: '1px solid #404656', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, backgroundColor: 'rgba(0, 207, 232, 0.12)' }}>
              <ConfirmationNumberIcon sx={{ color: '#00cfe8', fontSize: 20 }} />
            </Avatar>
            <Typography variant="h6" sx={{ color: '#d0d2d6', fontWeight: 600 }}>
              Aktivasyonlar
            </Typography>
          </Box>
          <Box sx={{ p: 2.5 }}>
            {activationsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={32} sx={{ color: '#7367f0' }} />
              </Box>
            ) : (
              <>
                {stats && (
                  <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <StatCard
                      label="Toplam"
                      value={stats.total}
                      color="#7367f0"
                      bg="rgba(115, 103, 240, 0.12)"
                      icon={<ConfirmationNumberIcon sx={{ color: '#7367f0', fontSize: 20 }} />}
                    />
                    <StatCard
                      label="Başarılı"
                      value={stats.completed}
                      color="#28c76f"
                      bg="rgba(40, 199, 111, 0.12)"
                      icon={<CheckCircleIcon sx={{ color: '#28c76f', fontSize: 20 }} />}
                    />
                    <StatCard
                      label="Bekleyen"
                      value={stats.pending}
                      color="#ff9f43"
                      bg="rgba(255, 159, 67, 0.12)"
                      icon={<HourglassEmptyIcon sx={{ color: '#ff9f43', fontSize: 20 }} />}
                    />
                    <StatCard
                      label="İptal"
                      value={stats.cancelled}
                      color="#ea5455"
                      bg="rgba(234, 84, 85, 0.12)"
                      icon={<CancelIcon sx={{ color: '#ea5455', fontSize: 20 }} />}
                    />
                    <StatCard
                      label="Zaman Aşımı"
                      value={stats.timeout}
                      color="#676d7d"
                      bg="rgba(103, 109, 125, 0.12)"
                      icon={<TimerOffIcon sx={{ color: '#676d7d', fontSize: 20 }} />}
                    />
                  </Box>
                )}

                {activations.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Servis</TableCell>
                          <TableCell>Ülke</TableCell>
                          <TableCell>Telefon</TableCell>
                          <TableCell>Kod</TableCell>
                          <TableCell>Maliyet</TableCell>
                          <TableCell>Durum</TableCell>
                          <TableCell>Tarih</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {activations.map((activation) => {
                          const sc = statusConfig[activation.status] || statusConfig.pending;
                          return (
                            <TableRow key={activation._id}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {activation.logo && (
                                    <img
                                      src={`${API_URL}${activation.logo}`}
                                      alt={activation.serviceName}
                                      style={{ width: 20, height: 20, borderRadius: 4 }}
                                    />
                                  )}
                                  <Typography variant="body2" sx={{ color: '#d0d2d6', fontWeight: 500 }}>
                                    {activation.serviceName}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {activation.countryIso ? (
                                  <img
                                    src={`https://flagcdn.com/24x18/${activation.countryIso}.png`}
                                    alt={activation.countryIso}
                                    style={{ width: 24, height: 18, borderRadius: 2 }}
                                  />
                                ) : (
                                  <Typography variant="body2" sx={{ color: '#676d7d' }}>-</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ color: '#d0d2d6', fontFamily: 'monospace' }}>
                                  {activation.phoneNumber}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {activation.code ? (
                                  <Chip
                                    label={activation.code}
                                    size="small"
                                    sx={{
                                      backgroundColor: 'rgba(115, 103, 240, 0.12)',
                                      color: '#7367f0',
                                      fontFamily: 'monospace',
                                      fontWeight: 600,
                                    }}
                                  />
                                ) : (
                                  <Typography variant="body2" sx={{ color: '#676d7d' }}>-</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ color: '#d0d2d6' }}>
                                  {activation.cost}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={sc.label}
                                  size="small"
                                  sx={{
                                    backgroundColor: sc.bg,
                                    color: sc.color,
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" sx={{ color: '#676d7d' }}>
                                  {new Date(activation.createdAt).toLocaleString('tr-TR')}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" sx={{ color: '#676d7d', textAlign: 'center', py: 2 }}>
                    Bu kullanıcıya ait aktivasyon bulunamadı
                  </Typography>
                )}
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

const InfoItem = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography variant="caption" sx={{ color: '#676d7d', mb: 0.5, display: 'block' }}>{label}</Typography>
    <Typography variant="body2" sx={{ color: '#d0d2d6', fontWeight: 500, wordBreak: 'break-all' }}>{value || '-'}</Typography>
  </Box>
);

export const UserShow = () => (
  <Show>
    <UserDetailContent />
  </Show>
);
